import React from "react";
import whyImage from "../../../../../images/web/why-sv.jpg";
import {
  FaArrowTrendUp,
  FaBookAtlas,
  FaMagnifyingGlassDollar,
  FaShield,
  FaCircleCheck,
  FaStar,
  FaArrowRight,
  FaCoins,
} from "react-icons/fa6";
import { US, CA, GB, AU, IE } from "country-flag-icons/react/3x2";

const WhyChooseSection = () => {
  const countries = [
    { name: "USA", Flag: US },
    { name: "Canada", Flag: CA },
    { name: "UK", Flag: GB },
    { name: "Australia", Flag: AU },
    { name: "Ireland", Flag: IE },
  ];

  return (
    <section className="bg-white py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* 4-Column Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 lg:gap-6">
          {/* Header Card (Blended into Grid) */}
          <div className="md:col-span-2 flex flex-col justify-center p-8 lg:p-10 bg-transparent rounded-3xl">
            {/* <span className="text-xs font-normal text-blue-600 uppercase tracking-widest mb-4 block">
              Why Us
            </span> */}
            <h2 className="text-h2 font-spartan font-light tracking-tight text-gray-900">
              Why <span className="text-blue-00 italic mx-1">Choose</span>{" "}
              <br className="sm:hidden" />
              SeamlessVisa?
            </h2>
            <p className="mt-6 text-base font-normal text-gray-600 max-w-md bg-transparent">
              Your future abroad, simplified and secured. We transform ambitious
              students into successful global applicants.
            </p>
          </div>

          {/* Card 1: Expert Guidance */}
          <div className="md:col-span-2 relative overflow-hidden bg-white border border-gray-100 hover:border-blue-200 rounded-4xl shadow-sm transition-all duration-300 flex flex-col justify-between p-8 group">
            <div className="absolute inset-0 z-0">
              <img
                src={whyImage}
                alt="Advisors with students"
                className="w-full h-full object-cover opacity-30 transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-linear-to-br from-white/80 via-white/60 to-blue-50/20"></div>
            </div>
            <div className="relative z-10 flex flex-col h-full">
              <div>
                <div className="w-12 h-12 bg-blue-50 border border-blue-100 rounded-xl flex items-center justify-center mb-6 shadow-sm">
                  <FaBookAtlas className="text-blue-600 text-xl" />
                </div>
                <h3 className="font-medium uppercase tracking-wide text-gray-900 mb-3">
                  Expert Guidance
                </h3>
                <p className="text-base font-normal text-gray-600 leading-relaxed max-w-md">
                  Our advisors have years of experience helping students gain
                  admission to top universities in the USA, Canada, United
                  Kingdom, Australia, and Ireland.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-2">
                {countries.map(({ name, Flag }) => (
                  <span
                    key={name}
                    className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full text-xs font-normal border border-gray-100 text-gray-700 shadow-sm"
                  >
                    <Flag className="w-4 h-3 rounded-[1px] object-cover" />
                    {name}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Card 2: Comprehensive Solution */}
          <div className="md:col-span-1 group bg-white border border-gray-100 hover:border-emerald-200 rounded-4xl shadow-sm p-8 transition-all duration-300 flex flex-col">
            <div className="w-12 h-12 bg-emerald-50 border border-emerald-100 rounded-xl flex items-center justify-center mb-6 shadow-sm">
              <FaShield className="text-emerald-600 text-xl" />
            </div>
            <h3 className="font-medium uppercase tracking-wide text-gray-900 mb-3">
              Comprehensive Solution
            </h3>
            <p className="text-gray-600 text-base font-normal grow leading-relaxed">
              We have the expertise and resources to assist you from your
              initial admission application to securing your student visa.
            </p>
            <div className="mt-6 pt-6 border-t border-gray-50">
              <ul className="space-y-3">
                {["Admission Strategy", "Visa Documentation"].map((item) => (
                  <li
                    key={item}
                    className="flex items-center gap-2 text-sm font-normal text-gray-700"
                  >
                    <FaCircleCheck className="text-base text-emerald-500 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Card 3: Proven Success Rate */}
          <div className="md:col-span-1 group bg-blue-600 hover:bg-blue-700 border border-blue-700 rounded-4xl shadow-sm p-8 transition-colors duration-300 flex flex-col relative overflow-hidden">
            <div className="absolute -right-4 -bottom-4 opacity-20 transform transition-transform duration-500 group-hover:scale-110">
              <FaArrowTrendUp className="text-[100px] text-white" />
            </div>
            <div className="relative z-10 flex flex-col h-full">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-6 backdrop-blur-md border border-white/20 shadow-xs">
                <FaStar className="text-white text-xl" />
              </div>
              <h3 className="font-medium uppercase tracking-wide text-white mb-2">
                Proven Success Rate
              </h3>
              <p className="text-blue-50 text-base font-normal leading-relaxed mb-6">
                Successful admissions to prestigious institutions and high visa
                approval rates.
              </p>
              <div className="mt-auto">
                <div className="text-4xl font-normal text-white font-spartan tracking-tight">
                  98%
                </div>
                <div className="text-xs font-normal uppercase tracking-widest text-blue-200 mt-1">
                  Approval Rate
                </div>
              </div>
            </div>
          </div>

          {/* Card 4: Affordable & Transparent Pricing */}
          <div className="md:col-span-2 group relative overflow-hidden bg-blue-ea/60 hover:bg-blue-ea/90 border border-blue-ea rounded-4xl shadow-sm p-8 transition-colors duration-300 flex flex-col justify-between">
            {/* Coins artifact — static piles on the floor with a few coins falling */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
            >
              {/* Base layer (kept for mobile — compact, right-aligned) */}
              {/* Falling coins */}
              <FaCoins className="absolute top-6 right-24 text-3xl text-amber-400/40 -rotate-12" />
              <FaCoins className="absolute top-2 right-44 text-xl text-amber-300/30 rotate-6" />
              <FaCoins className="absolute top-16 right-12 text-2xl text-amber-400/30 rotate-12" />
              {/* Pile on the floor — bottom-right corner */}
              <FaCoins className="absolute -bottom-3 right-6 text-7xl text-amber-500/40 transition-transform duration-500 group-hover:scale-110" />
              <FaCoins className="absolute -bottom-2 right-24 text-5xl text-amber-400/35" />
              <FaCoins className="absolute -bottom-4 right-40 text-4xl text-amber-300/30" />

              {/* Desktop layer — denser, spread leftward along the ground */}
              <div className="hidden sm:block">
                {/* More falling coins across the width */}
                <FaCoins className="absolute top-4 left-1/4 text-2xl text-amber-300/30 rotate-12" />
                <FaCoins className="absolute top-10 left-12 text-xl text-amber-300/25 -rotate-6" />
                <FaCoins className="absolute top-1 left-1/2 text-lg text-amber-400/25 rotate-3" />
                <FaCoins className="absolute top-20 right-60 text-2xl text-amber-300/25 -rotate-12" />
                {/* Extended pile along the floor — center toward the left */}
                <FaCoins className="absolute -bottom-3 right-56 text-6xl text-amber-400/35" />
                <FaCoins className="absolute -bottom-2 left-1/3 text-5xl text-amber-400/30" />
                <FaCoins className="absolute -bottom-4 left-24 text-6xl text-amber-500/35" />
                <FaCoins className="absolute -bottom-3 left-6 text-7xl text-amber-500/40" />
                <FaCoins className="absolute bottom-2 left-1/2 text-4xl text-amber-300/30" />
                <FaCoins className="absolute bottom-1 left-16 text-3xl text-amber-300/30" />
              </div>
            </div>
            <div className="relative z-10 flex flex-col sm:flex-row items-center sm:items-start gap-6 text-center sm:text-left h-full justify-between">
              <div>
                <div className="w-12 h-12 bg-amber-50 border border-amber-100 rounded-xl flex items-center justify-center mb-6 shadow-sm mx-auto sm:mx-0 text-amber-500">
                  <FaMagnifyingGlassDollar className="text-xl" />
                </div>
                <h3 className="font-medium uppercase tracking-wide text-gray-900 mb-2">
                  Affordable & Transparent Pricing
                </h3>
                <p className="text-gray-600 text-base font-normal">
                  No hidden fees – just quality services at the best rates.
                </p>
              </div>
              <div className="mt-auto sm:mt-0 sm:self-end w-full sm:w-auto">
                <button
                  onClick={() =>
                    document
                      .getElementById("pricing")
                      ?.scrollIntoView({ behavior: "smooth" })
                  }
                  className="bg-white text-blue-600 px-6 py-3 rounded-xl font-normal border border-blue-100 hover:border-blue-300 shadow-sm hover:bg-gray-50 transition-all flex items-center justify-center gap-2 cursor-pointer text-sm whitespace-nowrap w-full"
                >
                  View Pricing Plans
                  <FaArrowRight className="text-xs" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
